import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ProjectService } from '../../../clients/services/project.service';
import { ClientsService } from '../../../clients/services/clients.service';
import { FeiraClienteService } from '../../../clients/services/feira-cliente.service';
import { GetClientsDto, GetClientsResponseDto } from '../../../clients/dto/clients.dto';
import { FeiraClienteDto } from '../../../clients/dto/feira-cliente.dto';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Projeto } from '../../../clients/dto/project';
import { ContactService } from '../../../contacts/contact.service';
import { ContactList } from '../../../contacts/models/contact-list.model';

@Component({
  selector: 'app-projeto-form-modal',
  templateUrl: './projeto-form-modal.component.html',
  styleUrls: ['./projeto-form-modal.component.scss'],
  standalone: false
})
export class ProjetoFormModalComponent implements OnInit, OnDestroy, OnChanges {
  private _event: Projeto | null = null;
  
  @Input() 
  set event(value: Projeto | null) {
    this._event = value;
    if (this.projetoForm && value) {
      this.carregarDadosParaEdicao();
      this.verificarEdesabilitarStatus();
    }
  }
  
  get event(): Projeto | null {
    return this._event;
  }

  @Input() clienteId?: string; // ClienteId opcional - se fornecido, não mostra busca de cliente
  @Input() modoAdicionarContato?: boolean = false; // Modo para adicionar contato à lista
  @Input() listaContato?: ContactList; // Lista de contatos (necessário quando modoAdicionarContato = true)

  projetoForm!: FormGroup;
  private readonly destroy$ = new Subject<void>();

  // Clientes com scroll infinito
  clientesFiltrados: GetClientsDto[] = [];
  buscaCliente: string = '';
  dropdownClienteAberto: boolean = false;
  clienteSelecionado: GetClientsDto | null = null;
  clientePaginas = {
    paginaAtual: 1,
    totalPaginas: 1,
    carregandoMais: false,
    temMais: true
  };
  buscaClienteDebounce$ = new Subject<string>();

  // Feiras com scroll infinito
  feirasFiltradas: FeiraClienteDto[] = [];
  buscaFeira: string = '';
  dropdownFeiraAberto: boolean = false;
  feiraSelecionada: FeiraClienteDto | null = null;
  feiraPaginas = {
    paginaAtual: 1,
    totalPaginas: 1,
    carregandoMais: false,
    temMais: true
  };
  buscaFeiraDebounce$ = new Subject<string>();

  salvando = false;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private projectService: ProjectService,
    private clientsService: ClientsService,
    private feiraClienteService: FeiraClienteService,
    private toastService: ToastService,
    private http: HttpClient,
    private contactService: ContactService
  ) {
    // Formatar datas para input date (YYYY-MM-DD) se for edição
    const dataInicioFeira = this.event?.dataInicioFeira 
      ? new Date(this.event.dataInicioFeira).toISOString().split('T')[0] 
      : null;
    const dataFimFeira = this.event?.dataFimFeira 
      ? new Date(this.event.dataFimFeira).toISOString().split('T')[0] 
      : null;

    // Se for modo adicionar contato, feiraClienteId é obrigatório
    // Se for edição, feiraClienteId não é obrigatório (já que pode não ter)
    // Se for criação de projeto, feiraClienteId é obrigatório
    const feiraClienteIdValidators = this.modoAdicionarContato ? [Validators.required] : (this.event ? [] : [Validators.required]);
    // Se for edição, statusProjeto é obrigatório conforme contrato da API
    const statusProjetoValidators = this.event ? [Validators.required] : [];
    // Se clienteId for fornecido, não é obrigatório no form (já está definido)
    const clienteIdValidators = this.clienteId ? [] : [Validators.required];
    // Descrição só é obrigatória se não for modo adicionar contato
    const descricaoValidators = this.modoAdicionarContato ? [] : [Validators.required];

    this.projetoForm = this.fb.group({
      id: [this.event?.id || ''],
      descricao: [this.event?.descricao || '', descricaoValidators],
      clienteId: [this.clienteId || this.event?.clienteId || '', clienteIdValidators],
      feiraClienteId: [this.event?.feiraClienteId || '', feiraClienteIdValidators],
      statusProjeto: [this.event?.statusProjeto?.statusProjeto ?? '', statusProjetoValidators],
      comprimento: [this.event?.comprimento ?? ''],
      largura: [this.event?.largura ?? ''],
      comprimentoSobrepor: [this.event?.comprimentoSobrepor ?? ''],
      larguraSobrepor: [this.event?.larguraSobrepor ?? ''],
      dataInicioFeira: [dataInicioFeira],
      dataFimFeira: [dataFimFeira]
    }, { validators: [this.validarDatasFeira.bind(this), this.validarSobrepor.bind(this)] });
  }

  ngOnInit(): void {
    // Se clienteId foi fornecido, carregar feiras imediatamente
    if (this.clienteId) {
      this.carregarFeiras(true);
      // Se for edição, carregar dados do cliente e feira selecionados
      if (this.event) {
        this.carregarDadosClienteFeiraParaEdicao();
      }
    } else {
      // Configurar debounce para busca de clientes apenas se não tiver clienteId
      this.buscaClienteDebounce$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe((termo) => {
          this.carregarClientes(true);
        });
    }

    // Configurar debounce para busca de feiras
    this.buscaFeiraDebounce$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((termo) => {
        // Apenas filtrar localmente, não recarregar da API
        this.filtrarFeirasLocalmente();
      });

    // Observar mudanças no clienteId para carregar feiras (apenas se não tiver clienteId fixo)
    if (!this.clienteId) {
      this.projetoForm.get('clienteId')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((clienteId) => {
          if (clienteId) {
            this.projetoForm.patchValue({ feiraClienteId: '' }, { emitEvent: false });
            this.feiraSelecionada = null;
            this.buscaFeira = '';
            this.feirasFiltradas = [];
            this.feiraPaginas = {
              paginaAtual: 1,
              totalPaginas: 1,
              carregandoMais: false,
              temMais: true
            };
            this.carregarFeiras(true);
          } else {
            this.projetoForm.patchValue({ feiraClienteId: '' }, { emitEvent: false });
            this.feiraSelecionada = null;
            this.buscaFeira = '';
            this.feirasFiltradas = [];
          }
        });
    }

    // Verificar status após inicialização (caso event já esteja definido)
    this.verificarEdesabilitarStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando event é atribuído após ngOnInit, carregar dados e verificar status
    if (changes['event'] && !changes['event'].firstChange && this.projetoForm) {
      this.carregarDadosParaEdicao();
      this.verificarEdesabilitarStatus();
    }
    // Quando clienteId é atribuído, atualizar form
    if (changes['clienteId'] && this.projetoForm) {
      if (this.clienteId) {
        this.projetoForm.patchValue({ clienteId: this.clienteId }, { emitEvent: false });
        this.carregarFeiras(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarClientes(resetar: boolean = true): void {
    if (resetar) {
      this.clientePaginas = {
        paginaAtual: 1,
        totalPaginas: 1,
        carregandoMais: false,
        temMais: true
      };
      this.clientesFiltrados = [];
    }

    if (this.clientePaginas.carregandoMais || !this.clientePaginas.temMais) {
      return;
    }

    const termo = this.buscaCliente || '';
    
    let params = new HttpParams()
      .set('Paginacao.PaginaAtual', this.clientePaginas.paginaAtual.toString())
      .set('Paginacao.QuantidadePorPagina', '10')
      .set('OrdemAscendente', 'true');

    // Adicionar filtro de nome se houver termo de busca
    if (termo.trim() !== '') {
      params = params.set('Nome', termo.trim());
    }

    if (resetar) {
      this.clientePaginas.carregandoMais = true;
    }

    this.http.get<GetClientsResponseDto>(`${environment.apiUrl}/v1/clientes`, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const novosClientes = response.clientes || [];

          if (resetar) {
            this.clientesFiltrados = novosClientes;
          } else {
            this.clientesFiltrados = [...this.clientesFiltrados, ...novosClientes];
          }

          // Assumindo que a paginação vem na resposta
          const totalPaginas = Math.ceil((response.paginacao?.totalDeItens || 0) / 10);
          this.clientePaginas.totalPaginas = totalPaginas || 1;
          this.clientePaginas.temMais = this.clientePaginas.paginaAtual < this.clientePaginas.totalPaginas;
          this.clientePaginas.carregandoMais = false;
        },
        error: (error) => {
          console.error('Erro ao carregar clientes:', error);
          this.toastService.show('Erro ao carregar clientes', {
            classname: 'bg-danger text-light',
          });
          this.clientePaginas.carregandoMais = false;
        }
      });
  }

  carregarMaisClientes(): void {
    if (this.clientePaginas.carregandoMais || !this.clientePaginas.temMais) {
      return;
    }
    this.clientePaginas.paginaAtual++;
    this.carregarClientes(false);
  }

  onScrollClientes(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      this.carregarMaisClientes();
    }
  }

  abrirDropdownCliente(): void {
    this.dropdownClienteAberto = true;
    if (this.clientesFiltrados.length === 0) {
      this.carregarClientes(true);
    }
  }

  fecharDropdownCliente(): void {
    setTimeout(() => {
      this.dropdownClienteAberto = false;
    }, 200);
  }

  selecionarCliente(cliente: GetClientsDto): void {
    this.clienteSelecionado = cliente;
    this.buscaCliente = cliente.nome;
    this.projetoForm.patchValue({ clienteId: cliente.id });
    this.fecharDropdownCliente();
  }

  onBuscaClienteChange(): void {
    // Se o campo de busca for limpo e houver um cliente selecionado, limpar a seleção
    if (!this.buscaCliente || this.buscaCliente.trim() === '') {
      if (this.clienteSelecionado) {
        this.clienteSelecionado = null;
        this.projetoForm.patchValue({ clienteId: '' });
      }
    } else if (this.clienteSelecionado && this.buscaCliente !== this.clienteSelecionado.nome) {
      // Se o usuário alterar o texto e não corresponder ao cliente selecionado, limpar seleção
      this.clienteSelecionado = null;
      this.projetoForm.patchValue({ clienteId: '' });
    }
  }

  private todasFeiras: FeiraClienteDto[] = [];

  carregarFeiras(resetar: boolean = true): void {
    const clienteId = this.projetoForm.get('clienteId')?.value;
    if (!clienteId) {
      return;
    }

    if (resetar) {
      this.feiraPaginas = {
        paginaAtual: 1,
        totalPaginas: 1,
        carregandoMais: false,
        temMais: true
      };
      this.feirasFiltradas = [];
      this.todasFeiras = [];
    }

    if (this.feiraPaginas.carregandoMais || !this.feiraPaginas.temMais) {
      return;
    }

    if (resetar) {
      this.feiraPaginas.carregandoMais = true;
    }

    this.feiraClienteService.getFeirasCliente(clienteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (feiras) => {
          this.todasFeiras = feiras || [];
          this.filtrarFeirasLocalmente();
          this.feiraPaginas.temMais = false; // Feiras não têm paginação
          this.feiraPaginas.carregandoMais = false;
        },
        error: (error) => {
          console.error('Erro ao carregar feiras:', error);
          this.toastService.show('Erro ao carregar feiras', {
            classname: 'bg-danger text-light',
          });
          this.feiraPaginas.carregandoMais = false;
        }
      });
  }

  filtrarFeirasLocalmente(): void {
    if (!this.buscaFeira) {
      this.feirasFiltradas = [...this.todasFeiras];
    } else {
      const termoLower = this.buscaFeira.toLowerCase();
      this.feirasFiltradas = this.todasFeiras.filter(f =>
        f.nome?.toLowerCase().includes(termoLower)
      );
    }
  }

  abrirDropdownFeira(): void {
    const clienteId = this.projetoForm.get('clienteId')?.value;
    if (!clienteId) {
      this.toastService.show('Selecione um cliente primeiro', {
        classname: 'bg-warning text-light',
      });
      return;
    }
    this.dropdownFeiraAberto = true;
    if (this.feirasFiltradas.length === 0) {
      this.carregarFeiras(true);
    }
  }

  fecharDropdownFeira(): void {
    setTimeout(() => {
      this.dropdownFeiraAberto = false;
    }, 200);
  }

  selecionarFeira(feira: FeiraClienteDto): void {
    this.feiraSelecionada = feira;
    this.buscaFeira = feira.nome || '';
    this.projetoForm.patchValue({ feiraClienteId: feira.id });
    this.fecharDropdownFeira();
  }

  validarDatasFeira(control: AbstractControl): { [key: string]: any } | null {
    const dataInicio = control.get('dataInicioFeira')?.value;
    const dataFim = control.get('dataFimFeira')?.value;

    // Se um campo foi preenchido, o outro também deve ser preenchido
    if ((dataInicio && !dataFim) || (!dataInicio && dataFim)) {
      return { datasIncompletas: true };
    }

    if (dataInicio && dataFim) {
      const inicio = new Date(dataInicio);
      inicio.setHours(0, 0, 0, 0);
      const fim = new Date(dataFim);
      fim.setHours(0, 0, 0, 0);

      // Nunca permitir Data Fim Feira menor que Data Início Feira
      if (fim < inicio) {
        return { dataFimMenor: true };
      }
    }

    return null;
  }

  validarSobrepor(control: AbstractControl): { [key: string]: any } | null {
    const comprimentoSobrepor = control.get('comprimentoSobrepor')?.value;
    const larguraSobrepor = control.get('larguraSobrepor')?.value;

    // Verificar se os valores estão preenchidos (considerando strings vazias e null)
    const comprimentoPreenchido = comprimentoSobrepor && comprimentoSobrepor.toString().trim() !== '';
    const larguraPreenchida = larguraSobrepor && larguraSobrepor.toString().trim() !== '';

    // Se um campo foi preenchido, o outro também deve ser preenchido
    if ((comprimentoPreenchido && !larguraPreenchida) || (!comprimentoPreenchido && larguraPreenchida)) {
      return { sobreporIncompleto: true };
    }

    return null;
  }

  onDataInicioChange(): void {
    const dataInicio = this.projetoForm.get('dataInicioFeira')?.value;
    const dataFim = this.projetoForm.get('dataFimFeira')?.value;
    
    // Se data início foi preenchida e data fim não, preencher data fim com a mesma data
    if (dataInicio && !dataFim) {
      this.projetoForm.patchValue({ dataFimFeira: dataInicio });
    }
    
    // Validar novamente
    this.projetoForm.updateValueAndValidity();
  }

  onDataFimChange(): void {
    const dataInicio = this.projetoForm.get('dataInicioFeira')?.value;
    const dataFim = this.projetoForm.get('dataFimFeira')?.value;
    
    // Se data fim foi preenchida e data início não, preencher data início com a mesma data
    if (dataFim && !dataInicio) {
      this.projetoForm.patchValue({ dataInicioFeira: dataFim });
    }
    
    // Validar novamente
    this.projetoForm.updateValueAndValidity();
  }

  onComprimentoSobreporChange(): void {
    // Validar novamente após mudança no comprimento sobrepor
    this.projetoForm.updateValueAndValidity();
  }

  onLarguraSobreporChange(): void {
    // Validar novamente após mudança na largura sobrepor
    this.projetoForm.updateValueAndValidity();
  }

  getDataMinimaParaFim(): string | null {
    const dataInicio = this.projetoForm.get('dataInicioFeira')?.value;
    if (dataInicio) {
      return dataInicio;
    }
    return null;
  }

  carregarDadosParaEdicao(): void {
    if (!this.event || !this.projetoForm) return;

    // Formatar datas para input date (YYYY-MM-DD)
    const dataInicioFeira = this.event.dataInicioFeira 
      ? new Date(this.event.dataInicioFeira).toISOString().split('T')[0] 
      : null;
    const dataFimFeira = this.event.dataFimFeira 
      ? new Date(this.event.dataFimFeira).toISOString().split('T')[0] 
      : null;

    this.projetoForm.patchValue({
      id: this.event.id || '',
      descricao: this.event.descricao || '',
      clienteId: this.clienteId || this.event.clienteId || '',
      feiraClienteId: this.event.feiraClienteId || '',
      statusProjeto: this.event.statusProjeto?.statusProjeto ?? '',
      comprimento: this.event.comprimento ?? '',
      largura: this.event.largura ?? '',
      comprimentoSobrepor: this.event.comprimentoSobrepor ?? '',
      larguraSobrepor: this.event.larguraSobrepor ?? '',
      dataInicioFeira: dataInicioFeira,
      dataFimFeira: dataFimFeira
    }, { emitEvent: false });
  }

  carregarDadosClienteFeiraParaEdicao(): void {
    if (!this.event) return;

    // Se tiver feira selecionada, buscar e preencher
    if (this.event.feiraClienteId && this.clienteId) {
      this.feiraClienteService.getFeirasCliente(this.clienteId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (feiras) => {
            this.todasFeiras = feiras || [];
            this.feirasFiltradas = [...this.todasFeiras];
            const feira = feiras.find(f => f.id === this.event?.feiraClienteId);
            if (feira) {
              this.feiraSelecionada = feira;
              this.buscaFeira = feira.nome || '';
            }
          },
          error: (error) => {
            console.error('Erro ao carregar feiras para edição:', error);
          }
        });
    }
  }

  verificarEdesabilitarStatus(): void {
    if (this.event && this.projetoForm) {
      const statusValue = this.event.statusProjeto?.statusProjeto;
      if (this.isStatusBloqueado(statusValue)) {
        this.projetoForm.get('statusProjeto')?.disable({ emitEvent: false });
      } else {
        this.projetoForm.get('statusProjeto')?.enable({ emitEvent: false });
      }
    }
  }

  isStatusBloqueado(status: number | string | undefined | null): boolean {
    if (status === undefined || status === null) {
      return false;
    }
    const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
    // Status 2 (Reprovado) e 4 (Finalizado) não podem ser editados
    return statusNum === 2 || statusNum === 4;
  }

  onSubmit(): void {
    if (this.projetoForm.invalid) {
      this.projetoForm.markAllAsTouched();
      return;
    }

    this.salvando = true;
    
    // Usar getRawValue() para incluir campos desabilitados (como statusProjeto quando bloqueado)
    const formValue = this.event ? this.projetoForm.getRawValue() : this.projetoForm.value;

    // Converter valores da máscara (string com vírgula) para número
    let comprimento: number | null = null;
    if (formValue.comprimento && formValue.comprimento.toString().trim() !== '') {
      const valor = formValue.comprimento.toString().replace(/\./g, '').replace(',', '.');
      comprimento = parseFloat(valor) || null;
    }

    let largura: number | null = null;
    if (formValue.largura && formValue.largura.toString().trim() !== '') {
      const valor = formValue.largura.toString().replace(/\./g, '').replace(',', '.');
      largura = parseFloat(valor) || null;
    }

    let comprimentoSobrepor: number | null = null;
    if (formValue.comprimentoSobrepor && formValue.comprimentoSobrepor.toString().trim() !== '') {
      const valor = formValue.comprimentoSobrepor.toString().replace(/\./g, '').replace(',', '.');
      comprimentoSobrepor = parseFloat(valor) || null;
    }

    let larguraSobrepor: number | null = null;
    if (formValue.larguraSobrepor && formValue.larguraSobrepor.toString().trim() !== '') {
      const valor = formValue.larguraSobrepor.toString().replace(/\./g, '').replace(',', '.');
      larguraSobrepor = parseFloat(valor) || null;
    }

    // Converter datas para ISO string se preenchidas
    const dataInicioFeira = formValue.dataInicioFeira 
      ? new Date(formValue.dataInicioFeira).toISOString() 
      : null;
    const dataFimFeira = formValue.dataFimFeira 
      ? new Date(formValue.dataFimFeira).toISOString() 
      : null;

    // Modo adicionar contato à lista
    if (this.modoAdicionarContato && this.listaContato && this.contactService) {
      const payload = {
        clienteId: formValue.clienteId,
        feiraId: formValue.feiraClienteId,
        listaDeContatosGeradosId: this.listaContato.id
      };

      this.contactService.incluirContatoEmLista(payload).subscribe({
        next: () => {
          this.toastService.show('Contato adicionado à lista com sucesso', {
            classname: 'bg-success text-light',
          });
          this.activeModal.close(true);
        },
        error: (error) => {
          console.error('Erro ao adicionar contato:', error);
          const mensagem = error?.error?.errors?.Mensagens?.[0] || 'Erro ao adicionar contato à lista';
          this.toastService.show(mensagem, {
            classname: 'bg-danger text-light',
          });
          this.salvando = false;
        }
      });
      return;
    }

    if (this.event) {
      // Edição
      const statusProjetoValue = formValue.statusProjeto !== null && formValue.statusProjeto !== undefined && formValue.statusProjeto !== ''
        ? (typeof formValue.statusProjeto === 'number' ? formValue.statusProjeto : parseInt(formValue.statusProjeto))
        : 0;

      const projetoAtualizado: Projeto = {
        id: formValue.id,
        descricao: formValue.descricao,
        statusProjeto: {
          statusProjeto: statusProjetoValue
        } as any,
        dataInicioFeira: dataInicioFeira,
        dataFimFeira: dataFimFeira,
        comprimento: comprimento,
        largura: largura,
        comprimentoSobrepor: comprimentoSobrepor,
        larguraSobrepor: larguraSobrepor
      } as any;

      this.projectService.updateProject(projetoAtualizado)
        .subscribe({
          next: () => {
            this.toastService.show('Projeto atualizado com sucesso', {
              classname: 'bg-success text-light',
            });
            this.activeModal.close('updated');
          },
          error: (error) => {
            console.error('Erro ao atualizar projeto:', error);
            this.toastService.show('Erro ao atualizar projeto', {
              classname: 'bg-danger text-light',
            });
            this.salvando = false;
          },
          complete: () => {
            this.salvando = false;
          }
        });
    } else {
      // Criação
      this.projectService.createproject(
        {
          id: '', // ID vazio para criação
          descricao: formValue.descricao,
          feiraClienteId: formValue.feiraClienteId,
          comprimento: comprimento,
          largura: largura,
          comprimentoSobrepor: comprimentoSobrepor,
          larguraSobrepor: larguraSobrepor,
          dataInicioFeira: dataInicioFeira,
          dataFimFeira: dataFimFeira
        } as any,
        formValue.clienteId
      ).subscribe({
        next: () => {
          this.toastService.show('Projeto criado com sucesso', {
            classname: 'bg-success text-light',
          });
          this.activeModal.close('created');
        },
        error: (error) => {
          console.error('Erro ao criar projeto:', error);
          this.toastService.show('Erro ao criar projeto', {
            classname: 'bg-danger text-light',
          });
          this.salvando = false;
        },
        complete: () => {
          this.salvando = false;
        }
      });
    }
  }
}

